import { useEffect, useMemo, useState } from "react";
import { ArrowDownCircleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import CommonModal from "../common11/commonModals/CommonModal";
import EnhancedDynamicTablePagination from "../common/CommonTable/EnhancedDynamicTablePagination";
import { showErrorToast } from "../common11/toastUtils";
import { getOpIpPatients } from "../../services/opIpPatientService";

const mapOpPatientToQueueRow = (row) => ({
  visitId: row.admVitId,
  visitNo: row.ipOpNumber || "",
  uhid: row.regNoWithPrefix || "",
  patientName: row.patientName || "",
  mobile: "",
  gender: "",
  age: row.ageYear != null ? String(row.ageYear) : "",
  department: row.departmentName || "",
  doctor: row.admDoctorName || "",
  doctorId: row.doctorId,
  status: row.isDischarged ? "Discharged" : "Active",
  visitDate: row.admVitDate || "",
  tariffName: row.tariffName || "—",
  regId: row.regId,
  opdIpdId: row.admVitId,
  opdIpd: false,
  companyName: row.companyName || "—",
});

function OpdQueueListingModal({ isOpen, onClose, onSelectPatient }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [patientSearchText, setPatientSearchText] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await getOpIpPatients({
        opIpType: 0,
        isDischarged: false,
      });
      setRows((data || []).map(mapOpPatientToQueueRow));
    } catch (error) {
      setRows([]);
      showErrorToast(error.message || "Failed to load OPD patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setPatientSearchText("");
    setPage(0);
    loadRows();
  }, [isOpen]);

  const filteredData = useMemo(() => {
    const q = String(patientSearchText || "").trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((row) => {
      const haystack = [row.patientName, row.uhid, row.visitNo, row.mobile, row.doctor]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, patientSearchText]);

  const handleRowSelect = (row) => {
    onSelectPatient?.(row);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="OPD Patient Listing"
      size="xl"
      disableBackdropClose={false}
      showCloseButton={true}
      actions={[]}
    >
      <div className="space-y-3">
        <div className="relative w-full max-w-xl">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={patientSearchText}
            onChange={(e) => {
              setPatientSearchText(e.target.value);
              setPage(0);
            }}
            placeholder="Search by patient name / UHID / OPD no."
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm text-gray-700 outline-none focus:border-[#217E90] focus:ring-1 focus:ring-[#217E90]"
          />
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
          {loading ? (
            <div className="px-3 py-8 text-center text-sm text-gray-500">Loading OPD patients…</div>
          ) : null}
          <EnhancedDynamicTablePagination
            dataResult={filteredData}
            setDataResult={() => {}}
            fetchData={() => {}}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            count={filteredData.length}
            removeHeaders={["visitId", "regId", "opdIpdId", "opdIpd", "doctorId"]}
            showPaginationBar={true}
            stickyLeftColumns={["patientName", "uhid"]}
            highlightRow={true}
            customRowBgColor="#E9F5FE"
            customRowBorderColor="#217E90"
            onRowDoubleClick={handleRowSelect}
            renderActions={(row) => (
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => handleRowSelect(row)}
                  className="inline-flex items-center justify-center text-[#217E90] hover:text-[#1a5e70]"
                  title="Select patient"
                >
                  <ArrowDownCircleIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            actionsPosition="start"
            stickyActions={true}
            tableClass="min-h-[320px]"
          />
        </div>
      </div>
    </CommonModal>
  );
}

export default OpdQueueListingModal;
